// Implements: F-GEN-033, F-UIX-009, F-UIX-010
/**
 * The three reads of the drawer's diagram tabs, each under its shared cache key so the rest of
 * the page and these tabs never disagree about one version.
 *
 * - `useVersionPieces`: the pieces of ONE version. `pending` until it answers; a failed read is
 *   "no pieces", so the draw is offered and the server answers it with its own refusal.
 * - `useVersionNumbers`: the versions of the mockup, newest first, for the picker. Empty while
 *   read or when the read fails: the picker then shows only the version on screen.
 * - `useMockupBrief`: the mockup's brief to prefill the draw (P6-2); `undefined` until it answers,
 *   `""` when it fails or there is none.
 */

import { useQuery } from "@tanstack/react-query";

import type { ApiError } from "../../../shared/api/ApiError";
import { versionDetailKey, versionTreeKey } from "../../../shared/api/queryKeys";
import type { VersionDetailView, VersionTreeNode } from "../../../types/api";
import { fetchMockupBrief, fetchVersionArtifacts, fetchVersionNodes } from "../api/versionArtifactsApi";

export type VersionPieces =
  | { readonly status: "pending" }
  | { readonly status: "read"; readonly detail: VersionDetailView | null };

export function useVersionPieces(mockupId: string, versionId: string): VersionPieces {
  const version = useQuery<VersionDetailView, ApiError>({
    queryKey: versionDetailKey(mockupId, versionId),
    queryFn: () => fetchVersionArtifacts(mockupId, versionId),
  });
  if (version.error !== null) return { status: "read", detail: null };
  if (version.data === undefined) return { status: "pending" };
  return { status: "read", detail: version.data };
}

export interface VersionNumber {
  readonly id: string;
  readonly n: number;
}

export function useVersionNumbers(mockupId: string): readonly VersionNumber[] {
  const tree = useQuery<readonly VersionTreeNode[], ApiError>({
    queryKey: versionTreeKey(mockupId),
    queryFn: () => fetchVersionNodes(mockupId),
  });
  return [...(tree.data ?? [])]
    .sort((left, right) => right.sequence_number - left.sequence_number)
    .map((node) => ({ id: node.id, n: node.sequence_number }));
}

/** Not a shared key: no other module reads the brief this way, and it never goes stale in a session. */
function mockupBriefKey(mockupId: string): readonly ["diagrams", "brief", string] {
  return ["diagrams", "brief", mockupId];
}

export function useMockupBrief(mockupId: string): string | undefined {
  const brief = useQuery<string, ApiError>({
    queryKey: mockupBriefKey(mockupId),
    queryFn: () => fetchMockupBrief(mockupId),
    staleTime: Number.POSITIVE_INFINITY,
  });
  if (brief.error !== null) return "";
  return brief.data;
}
